import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { Prompt } from "react-router-dom";
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import { Autocomplete } from "@material-ui/lab";
import TabHeader from "src/components/TabHeader/TabHeader";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import TransitionDialog from "src/components/AlertDialog/TransitionDialog";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { useAppSelector } from "src/app-redux/hooks";
import { NodCommercialRoute } from "../../nod-commercial.routes";
import { useStyles } from "../TerminalCharges/components/style";
import { filterBy, numberFormatting } from "src/common/utils/helpers";
import {
  selectLoader,
  selectPaymentTypes,
  selectWeightScanTypes,
  addPaymentAction,
  removeAllPayments,
} from "src/app-redux/commercial/commercialSlice";
import {
  getPaymentTypesDropdownAction,
  postTerminalChargesWholeRecordAction,
} from "src/app-redux/commercial/actions/commercialActions";
import {
  selectAllPartyNameRowsList,
  selectAllTaxTypeRowsList,
} from "src/app-redux/configuration/configurationSlice";
import {
  getAllPartName,
  getAllTaxTypes,
} from "src/app-redux/configuration/actions/configurationActions";
import { filteryByArr } from "src/common/utils/app.dropdowns";

export function ForwarderWiseCharges(): React.ReactElement {
  const FORWARDER_WISE_AGENT_ID = 21003;
  const SELECT_ALL_CONTAINERS_OPTION = "__select_all_containers__";
  const classes = useStyles();
  const dispatch = useDispatch();
  const moduleName = NodCommercialRoute.title;
  const pageHeading = "Forwarder Wise Invoice";
  const loading = useAppSelector(selectLoader);
  const allPartyNameRows = useAppSelector(selectAllPartyNameRowsList);
  const allTaxTypeRows = useAppSelector(selectAllTaxTypeRowsList);
  const paymentTypes: any[] = useAppSelector(selectPaymentTypes);
  const weightScanTypesRecord: any[] = useAppSelector(selectWeightScanTypes);

  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";

  const [loader2, setLoader2] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const [dialogContent, setDialogContent] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);

  const [pendingContainerList, setPendingContainerList] = useState<any[]>([]);
  const [selectedContainers, setSelectedContainers] = useState<any[]>([]);
  const [partyId, setPartyId] = useState<string>("");
  const [filterByValue, setFilterByValue] = useState<string>("today");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const containerFieldWrapRef = useRef<HTMLDivElement | null>(null);

  const [filterGridRecord, setFilterGridRecord] = useState<any[]>([]);
  const [apiSubmitRecordData, setApiSubmitRecordData] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [tax, setTax] = useState<any>({ taxType: "", taxRate: 0 });
  const [discount, setDiscount] = useState<number>(0);
  const [remarks, setRemarks] = useState<string>("");
  const [currency, setCurrency] = useState<string>("PKR");

  const columns = useMemo(
    () => [
      { accessorKey: "paymentType", header: "Type of Payment", flex: 1 },
      { accessorKey: "paymentQuantity", header: "Quantity", flex: 1 },
      { accessorKey: "charges", header: "Charges", flex: 1 },
    ],
    []
  );

  const forwarderDisplayName = useMemo(() => {
    if (!partyId) return "-";
    const party = allPartyNameRows.find(
      (p: { id?: number | string; name?: string }) => String(p.id) === String(partyId)
    );
    return (party as { name?: string } | undefined)?.name || "-";
  }, [partyId, allPartyNameRows]);

  const allContainersSelected =
    pendingContainerList.length > 0 && selectedContainers.length === pendingContainerList.length;

  const linkedContainerOptions = useMemo(
    () => [
      {
        id: SELECT_ALL_CONTAINERS_OPTION,
        containerNumber: "Select All",
      },
      ...(pendingContainerList || []),
    ],
    [pendingContainerList]
  );

  const containersSummary = useMemo(() => {
    const list = selectedContainers.length > 0 ? selectedContainers : pendingContainerList || [];

    // Total Containers = simple count of all rows returned for selected forwarder.
    const totalContainers = list.length;

    // Category = size-wise breakdown from `containerSize`.
    // Example: two 20ft + one 40ft => "2x20, 1x40"
    const sizeCounts = new Map<number, number>();
    list.forEach((row: any) => {
      const sz = row?.containerSize;
      if (sz === undefined || sz === null || sz === "") return;
      const n = Number(sz);
      if (Number.isNaN(n)) return;
      sizeCounts.set(n, (sizeCounts.get(n) || 0) + 1);
    });
    const categoryLabel =
      sizeCounts.size === 0
        ? "-"
        : Array.from(sizeCounts.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([size, count]) => `${count}x${size}`)
            .join(", ");

    // Total Vehicles = UNIQUE count of `vehicleNumber` across all returned containers.
    // One GateIn Payment should be charged per unique vehicle.
    const vehicleKeys = list
      .map((r: any) => r?.vehicleNumber)
      .filter((v: any) => v != null && String(v).trim() !== "");
    const totalVehiclesUnique = new Set(vehicleKeys.map((v: any) => String(v).trim())).size;
    return { totalContainers, categoryLabel, totalVehiclesUnique };
  }, [pendingContainerList, selectedContainers]);

  const getPaymentTypeIdByName = (name: string): number | null => {
    const match = (paymentTypes || []).find(
      (pt: any) =>
        String(pt?.name || "")
          .trim()
          .toLowerCase() === name.trim().toLowerCase()
    );
    const id = match?.id ?? match?.paymentTypeId;
    const num = Number(id);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  const getPaymentTypeAmountById = async (id: number): Promise<number> => {
    const res = await fetchWrapper.get(`get-payment-type-by-id/${id}`);
    if (!res?.isSuccess) throw new Error(res?.message || "Failed to load payment type details");
    const amount = Number(res?.value?.amount ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  };

  const applyCharges = async () => {
    if (!partyId || selectedContainers.length === 0) {
      dispatch(
        addNotificationAction({
          message: "Please select Forwarder and at least one container first.",
          options: { variant: "error" },
        })
      );
      return;
    }
    // We always rebuild the payment rows from scratch so the grid reflects the latest
    // forwarder/container list (no manual cleanup needed).
    dispatch(removeAllPayments());

    // ---------- Quantity calculation (THIS is the "formula") ----------
    // - Weight/Scan payments: per container => quantity = totalContainers
    // - Unloading payments: per container size => quantity = count of that size
    // - GateIn payment: per UNIQUE vehicle => quantity = uniqueVehicles
    const containers = selectedContainers;
    const totalContainers = containers.length;
    const uniqueVehicles = containersSummary.totalVehiclesUnique;
    const countBySize = containers.reduce((acc: Record<string, number>, row: any) => {
      const key = String(row?.containerSize ?? "").trim();
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const qty20 = countBySize["20"] || 0;
    const qty40 = countBySize["40"] || 0;
    const qty45 = countBySize["45"] || 0;

    // Payment types to create, along with the computed quantity per rule.
    // NOTE: charges are NOT hard-coded here — we fetch the configured "amount per unit"
    // from `get-payment-type-by-id/{paymentTypeId}`.
    const rules: Array<{ name: string; quantity: number }> = [
      { name: "Weight Payment", quantity: totalContainers },
      { name: "Scan Payment", quantity: totalContainers },
      { name: "Unloading 20ft Payment", quantity: qty20 },
      { name: "Unloading 40ft Payment", quantity: qty40 },
      { name: "Unloading 45ft Payment", quantity: qty45 },
      { name: "GateIn Payment", quantity: uniqueVehicles },
    ].filter((r) => r.quantity > 0);

    try {
      setLoader2(true);

      for (const rule of rules) {
        // 1) Find paymentTypeId by *exact* configured name
        const paymentTypeId = getPaymentTypeIdByName(rule.name);
        if (!paymentTypeId) {
          dispatch(
            addNotificationAction({
              message: `Payment type not found in configuration: ${rule.name}`,
              options: { variant: "error" },
            })
          );
          continue;
        }

        // 2) Fetch the configured amount per unit (EUC) for that payment type
        const amountPerUnit = await getPaymentTypeAmountById(paymentTypeId);

        // 3) Add a single row to the grid.
        // The grid shows:
        // - Type of Payment = rule.name
        // - Quantity = rule.quantity
        // - Charges = "amountPerUnit" (per unit); total shown elsewhere is computed using quantity.
        dispatch(
          addPaymentAction({
            paymentTypeId,
            paymentType: rule.name,
            paymentQuantity: rule.quantity,
            charges: amountPerUnit,
            currency,
            discount: 0,
            discountedCharges: amountPerUnit,
          })
        );
      }

      dispatch(
        addNotificationAction({
          message: "Charges applied",
          options: { variant: "success" },
        })
      );
      setIsDirty(true);
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Failed to apply charges",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  /** Normalize API rows for dropdown (label: containerNumber). */
  const normalizeForwarderContainerRows = (raw: any): any[] => {
    const list = Array.isArray(raw)
      ? raw
      : raw?.containersList ||
        raw?.list ||
        raw?.containerList ||
        raw?.containers ||
        raw?.data ||
        [];
    return (list || []).map((row: any, idx: number) => {
      const containerNumber =
        row?.containerNumber ?? row?.containerNo ?? row?.container ?? row?.name ?? "";
      return {
        ...row,
        id: row?.id ?? row?.gatePassId ?? `${containerNumber}-${idx}`,
        containerNumber,
      };
    });
  };

  const fetchContainersByForwarderId = async (forwarderId: string) => {
    setPendingContainerList([]);
    setSelectedContainers([]);
    if (!forwarderId) return;

    try {
      setLoader2(true);
      const params = new URLSearchParams();
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      const query = params.toString();
      const results = await fetchWrapper.get(
        `get-all-containers-by-forwarder-id/${encodeURIComponent(forwarderId)}${
          query ? `?${query}` : ""
        }`
      );
      if (results?.isSuccess) {
        const rows = normalizeForwarderContainerRows(results.value);
        setPendingContainerList(rows);
        setSelectedContainers([]);
      } else {
        setPendingContainerList([]);
        setSelectedContainers([]);
        dispatch(
          addNotificationAction({
            message: results?.message || "Could not load containers for this forwarder.",
            options: { variant: "error" },
          })
        );
      }
    } catch {
      setPendingContainerList([]);
      setSelectedContainers([]);
      dispatch(
        addNotificationAction({
          message: "Could not load containers for this forwarder.",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  const calculateTax = () => {
    const amt = Number(totalAmount) || 0;
    const taxRate = Number(tax?.taxRate) || 0;
    return Math.round((amt * taxRate) / 100);
  };

  const calculateNetAmount = () => {
    const amt = Number(totalAmount) || 0;
    const taxAmt = calculateTax();
    const disc = Number(discount) || 0;
    return Math.round(amt + taxAmt - disc);
  };

  const recordSaveHandler = () => {
    let grossTotal = 0;
    let discountTotal = 0;

    const newData = (weightScanTypesRecord || []).map((item: any) => {
      const quantity = item.paymentQuantity || 1;
      const discountPerUnit = item.discount || 0;
      const totalDiscount = discountPerUnit * quantity;
      const totalCharges = (item.charges || 0) * quantity;

      grossTotal += totalCharges;
      discountTotal += totalDiscount;

      return {
        ...item,
        charges: `${item.currency || "PKR"} ${numberFormatting(totalCharges)} `,
      };
    });

    setTotalAmount(grossTotal);
    setDiscount(discountTotal);
    setFilterGridRecord(newData);

    const newData2 = (weightScanTypesRecord || []).map((item: any) => {
      const quantity = item.paymentQuantity || 1;
      return {
        ...item,
        charges: Math.round((item.charges || 0) * quantity),
        discount: Math.round((item.discount || 0) * quantity),
      };
    });
    setApiSubmitRecordData(newData2);
  };

  const resetWholePage = () => {
    setPartyId("");
    setFilterByValue("today");
    const range = filterBy({ value: "today" });
    setStartDate(range?.startDate || "");
    setEndDate(range?.endDate || "");
    setIsCustomDate(false);
    setPendingContainerList([]);
    setSelectedContainers([]);
    setNominationAndVehicle();
    setFilterGridRecord([]);
    setApiSubmitRecordData([]);
    setTotalAmount(0);
    setTax({ taxType: "", taxRate: 0 });
    setDiscount(0);
    setRemarks("");
    setCurrency("PKR");
    setIsDirty(false);
    dispatch(removeAllPayments());
  };

  const setNominationAndVehicle = () => {
    // placeholders to keep TerminalDetails stable (no-op)
  };

  const filterByAction = (data: any) => {
    setFilterByValue(data?.value || "");
    if (data && data.value) {
      const res = filterBy(data);
      if (res?.isCustomDate) {
        setIsCustomDate(true);
      } else {
        setIsCustomDate(false);
      }
      setStartDate(res?.startDate || "");
      setEndDate(res?.endDate || "");
    }
  };

  const submitCompletePage = async () => {
    if (!apiSubmitRecordData.length) {
      dispatch(
        addNotificationAction({
          message: "Please add some Payment record!",
          options: { variant: "error" },
        })
      );
      return;
    }

    let data = "";
    apiSubmitRecordData.forEach((item: any) => {
      data = data.concat(
        `${item.paymentType}: ${item.currency || currency} ${numberFormatting(item.charges)} #`
      );
    });
    data = data.concat(` Total Charges: ${currency} ${numberFormatting(calculateNetAmount())}`);
    setDialogContent(data);
    setIsOpen(true);
  };

  const handleConfirmDialog = async () => {
    // close confirmation dialog immediately; show loader via backdrop
    setIsOpen(false);
    if (!partyId) {
      dispatch(
        addNotificationAction({
          message: "Please select Forwarder first.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (!apiSubmitRecordData.length) {
      dispatch(
        addNotificationAction({
          message: "Please add some Payment record!",
          options: { variant: "error" },
        })
      );
      return;
    }

    const forwarderId = (() => {
      const n = Number(partyId);
      return Number.isFinite(n) && n > 0 ? n : partyId;
    })();

    const list = selectedContainers || [];
    const rowsWithGatePass = list.filter((r: any) => {
      const id = r?.gatePassId ?? r?.gatePassID ?? r?.gatePassid;
      return id !== undefined && id !== null && String(id).trim() !== "";
    });
    const gatePassIds = rowsWithGatePass
      .map((r: any) => String(r?.gatePassId ?? r?.gatePassID ?? r?.gatePassid).trim())
      .join(",");
    const vehicleNumber = [
      ...new Set(
        rowsWithGatePass
          .map((r: any) => String(r?.vehicleNumber ?? "").trim())
          .filter((v: string) => v !== "")
      ),
    ].join(",");

    const gatePassId = rowsWithGatePass[0]
      ? String(
          rowsWithGatePass[0]?.gatePassId ??
            rowsWithGatePass[0]?.gatePassID ??
            rowsWithGatePass[0]?.gatePassid
        ).trim()
      : "";

    const record = {
      gatePassId,
      forwarderId,
      // backend `/create-payment` historically uses `partyId` for forwarder
      partyId: forwarderId,
      agentId: FORWARDER_WISE_AGENT_ID,
      gatePassIds,
      vehicleNumber,
      gatePassPaymentDetail: apiSubmitRecordData,
      remarks,
      taxId: tax?.id,
      discount: Number(discount) || 0,
      taxAmount: calculateTax(),
      taxRate: Number(tax?.taxRate) || 0,
      netAmount: calculateNetAmount(),
      totalAmount,
      currency,
    };

    try {
      setLoader2(true);
      // Don't rely on RTK's `.unwrap()` here (some store/dispatch typings set-ups don't support it).
      // Instead, inspect the thunk action result.
      const action: any = await dispatch(postTerminalChargesWholeRecordAction(record) as any);
      const status = action?.meta?.requestStatus;
      if (status !== "fulfilled") {
        throw new Error(
          action?.error?.message || action?.payload?.message || "Failed to create payment"
        );
      }

      dispatch(
        addNotificationAction({
          message: "Payment created",
          options: { variant: "success" },
        })
      );

      // Fully reset to avoid showing any previous info after save
      setDialogContent("");
      resetWholePage();
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Failed to create payment",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    return (row || []).map((data: any) => ({
      "Type of Payment": data?.paymentType,
      Quantity: data?.paymentQuantity,
      Charges: data?.charges,
    }));
  };

  useEffect(() => {
    const range = filterBy({ value: "today" });
    setStartDate(range?.startDate || "");
    setEndDate(range?.endDate || "");
  }, []);

  useEffect(() => {
    // Clear any previously-added payments when entering this screen,
    // so navigating back doesn't show stale grid rows.
    dispatch(removeAllPayments());
    dispatch(getAllPartName(true));
    dispatch(getPaymentTypesDropdownAction());
    dispatch(getAllTaxTypes(1));
    return () => {
      dispatch(removeAllPayments());
    };
  }, []);

  useEffect(() => {
    recordSaveHandler();
  }, [weightScanTypesRecord]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  return (
    <div>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <TabHeader module={moduleName} heading={pageHeading} link={NodCommercialRoute.path} />
      <Backdrop className={classes.backdrop} open={loading || loader2}>
        <CommonLoader />
      </Backdrop>
      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetWholePage()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => submitCompletePage()}
                >
                  Save
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <Box style={{ marginBottom: "10px" }}>
            <Divider />
          </Box>

          <Grid spacing={2} container direction="row">
            <Grid item xs={12} md={6}>
              <Grid spacing={2} container direction="row">
                <Grid item xs={12} md={3}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Filter by"
                      placeholder="Filter by"
                      name="filterBy"
                      disableClearable={true}
                      optionsType={filteryByArr}
                      getOptionLabel={(option: any) => option.label || ""}
                      value={filteryByArr.find(({ value }: any) => value === filterByValue) || ""}
                      onChange={(row: any) => {
                        if (row === null) {
                          setFilterByValue("");
                          return;
                        }
                        filterByAction(row);
                        setIsDirty(true);
                      }}
                    />
                  </Box>
                </Grid>

                {isCustomDate && (
                  <Grid item xs={12} md={3}>
                    <CommonTextFieldDate
                      cssClassName="customTextField"
                      label={"Start Date"}
                      name={"startDate"}
                      datePickerRef={startDatePickerRef}
                      value={startDate}
                      onChange={(e: any) => {
                        setStartDate(moment(e).format("YYYY-MM-DDTHH:mm"));
                        setEndDate("");
                        setIsDirty(true);
                      }}
                      maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                    />
                  </Grid>
                )}

                {isCustomDate && (
                  <Grid item xs={12} md={3}>
                    <CommonTextFieldDate
                      cssClassName="customTextField"
                      label={"End Date"}
                      name={"endDate"}
                      datePickerRef={endDatePickerRef}
                      value={endDate}
                      onChange={(e: any) => {
                        setEndDate(moment(e).format("YYYY-MM-DDTHH:mm"));
                        setIsDirty(true);
                      }}
                      minDate={startDate}
                      maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                    />
                  </Grid>
                )}

                <Grid item xs={12} md={3}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Forwarder"
                      placeholder="Select Forwarder Name"
                      name="PartyId"
                      optionsType={allPartyNameRows}
                      getOptionLabel={(option: any) => option.name || ""}
                      value={
                        allPartyNameRows.find((p: any) => String(p.id) === String(partyId)) || null
                      }
                      onChange={(option: any) => {
                        if (!option) {
                          setPartyId("");
                          setPendingContainerList([]);
                          setSelectedContainers([]);
                          setIsDirty(true);
                          return;
                        }
                        const next = String(option?.id ?? "");
                        setPartyId(next);
                        setIsDirty(true);
                        void fetchContainersByForwarderId(next);
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box width="100%">
                    <div ref={containerFieldWrapRef} style={{ width: "100%" }}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={linkedContainerOptions}
                        value={selectedContainers}
                        onChange={(_e, nextValue) => {
                          const rows = nextValue || [];
                          const hasSelectAll = rows.some(
                            (row: any) => row?.id === SELECT_ALL_CONTAINERS_OPTION
                          );

                          if (hasSelectAll) {
                            setSelectedContainers(
                              allContainersSelected ? [] : [...(pendingContainerList || [])]
                            );
                          } else {
                            setSelectedContainers(rows);
                          }
                          setIsDirty(true);
                        }}
                        getOptionSelected={(option: any, value: any) =>
                          String(option?.id ?? option?.containerNumber ?? "") ===
                          String(value?.id ?? value?.containerNumber ?? "")
                        }
                        getOptionLabel={(option: any) => String(option?.containerNumber || "")}
                        renderOption={(option: any, { selected }) => {
                          const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
                          const checkedIcon = <CheckBoxIcon fontSize="small" />;
                          const isSelectAllOption = option?.id === SELECT_ALL_CONTAINERS_OPTION;
                          return (
                            <React.Fragment>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={isSelectAllOption ? allContainersSelected : selected}
                              />
                              {option?.containerNumber || "-"}
                            </React.Fragment>
                          );
                        }}
                        renderTags={(value: any[], getTagProps: any) => {
                          const normalized = (value || [])
                            .map((v: any) => String(v?.containerNumber || v || "").trim())
                            .filter(Boolean);
                          if (!normalized.length) return null;

                          const first = normalized[0];
                          const remaining = normalized.slice(1);
                          const remainingCount = remaining.length;

                          return (
                            <>
                              <Chip
                                size="small"
                                label={first}
                                {...getTagProps({ index: 0 })}
                                style={{ maxWidth: 180 }}
                              />
                              {remainingCount > 0 && (
                                <Tooltip
                                  placement="top"
                                  title={
                                    <div
                                      style={{
                                        whiteSpace: "pre-line",
                                        fontSize: 14,
                                        lineHeight: 1.6,
                                        padding: "8px 10px",
                                        minWidth: 220,
                                        maxWidth: 360,
                                        maxHeight: 240,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {remaining.join("\n")}
                                    </div>
                                  }
                                >
                                  <Chip
                                    size="small"
                                    label={`+${remainingCount} more`}
                                    style={{ marginLeft: 6 }}
                                  />
                                </Tooltip>
                              )}
                            </>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="customTextField"
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Linked Container"
                            placeholder="Select container(s)"
                            InputLabelProps={{ shrink: true }}
                            disabled={!partyId || pendingContainerList.length === 0}
                          />
                        )}
                      />
                    </div>
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box
                    textAlign="inherit"
                    mt={1}
                    mb={1}
                    className="terminalChargesSearchBtns headerButtonsConfig"
                    style={{ float: "right" }}
                  >
                    <CommonButton
                      cssClass="customSubmitBtn"
                      isGreen
                      disable={!partyId || selectedContainers.length === 0}
                      onClick={() => void applyCharges()}
                    >
                      Apply Charges
                    </CommonButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                style={{
                  padding: "20px",
                  backgroundColor: "rgb(0 0 0 / 7%)",
                  border: "none",
                }}
              >
                <Grid spacing={2} container direction="row" justifyContent="space-between">
                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Forwarder:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right">{forwarderDisplayName}</Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Total Containers:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right">
                        {partyId ? containersSummary.totalContainers : "-"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Category:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right" style={{ wordBreak: "break-word" }}>
                        {partyId ? containersSummary.categoryLabel : "-"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Total Vehicles:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right">
                        {partyId ? containersSummary.totalVehiclesUnique : "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <div className={classes.grid}>
            <CommonMaterialGrid
              rows={filterGridRecord}
              columns={columns}
              defaultPerPageRecord={50}
              height={450}
              isExport={isExport}
              exportHandler={exportHandler}
              filename={`Forwarder_Wise_Invoice_${Date.now()}`}
            />
          </div>

          <Grid spacing={2} container direction="row">
            <Grid item xs={4} md={4} className={classes.remarksDiv}>
              <Box>
                <TextField
                  className="customTextField"
                  size="small"
                  fullWidth
                  multiline
                  rows={1}
                  rowsMax={5}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="Remarks"
                  name="remarks"
                  placeholder="Remarks"
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value);
                    setIsDirty(true);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4} md={4} className={classes.remarksDiv}>
              <Box>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Tax Type (%)"
                  placeholder="Select Tax Type"
                  name="taxTypeId"
                  optionsType={allTaxTypeRows}
                  getOptionLabel={(option: any) =>
                    `${option.taxType.toString()} - ${option?.taxRate.toString()}%`
                  }
                  onChange={(row: any) => {
                    setTax(row || { taxType: "", taxRate: 0 });
                    setIsDirty(true);
                  }}
                  value={allTaxTypeRows.find((st: any) => st.id == tax?.id) || null}
                />
              </Box>
            </Grid>
            <Grid item xs={4} md={4} className={classes.remarksDiv}>
              <Box>
                <TextField
                  className="customTextField"
                  type="number"
                  InputProps={{ inputProps: { min: 0, max: Number(totalAmount) } }}
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="Discount"
                  name="discount"
                  placeholder="Discount"
                  value={discount}
                  onChange={(e) => {
                    setDiscount(Number(e.target.value) || 0);
                    setIsDirty(true);
                  }}
                  onPaste={(e: any) => {
                    if (/[^0-9]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid
            item
            md={12}
            container
            className={`${classes.totalDiv} ${"commercialChargeTotalDiv"}`}
          >
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                Total <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{`${numberFormatting(
                  Number(totalAmount)
                )} `}</span>
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                STax Amount <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{` ${numberFormatting(calculateTax())} `}</span>
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                Discount <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{` ${numberFormatting(
                  Number(discount) || 0
                )} `}</span>
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                Net Amount <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{` ${numberFormatting(
                  calculateNetAmount()
                )} `}</span>
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TransitionDialog
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        heading={"Are you sure you want to save the following charges?"}
        handleConfirm={handleConfirmDialog}
      >
        {dialogContent &&
          dialogContent?.split("#").map((data: any, index: any) => (
            <Box key={index}>
              <p style={{ lineHeight: "0.7" }}>{data}</p>
            </Box>
          ))}
      </TransitionDialog>
    </div>
  );
}
